/**
 * 隱私分類：屬於它的作品不出現在預設清單裡。
 *
 * 這**不是存取控制**。作品、閱讀進度、書籤、備份狀態與雲端副本都不受影響，直接開啟
 * 閱讀器網址仍然讀得到，API 也照常回傳它。擋的是「打開書庫時它出現在畫面上」。
 *
 * 判斷只寫這一處。清單由兩個來源合併——雲端目錄與本機尚未註冊的作品——規則若分別實作
 * 在 API 與前端，兩份必須永遠一致，而它們會漂移。
 */

export type PrivacyInput = {
  /** 這一本屬於哪些分類。 */
  categoryIds: readonly string[]
  /** 哪些分類是隱私分類。 */
  privateCategoryIds: ReadonlySet<string>
  /** 目前篩選選了哪些分類。 */
  selectedCategoryIds: readonly string[]
}

/**
 * 這一本現在該不該出現在清單裡。
 *
 * 不屬於任何隱私分類就照舊。屬於的話，只有當篩選選中了**它所屬的**某個隱私分類時才出現
 * ——選了隱私分類 P 不該讓 P 以外的隱私分類裡的東西跟著現身，那會讓兩個隱私分類互相洩漏。
 *
 * 搜尋字串不在考慮之內：搜尋正是有人要找它時會做的事，讓搜尋翻得出來等於這個功能只擋住
 * 漫不經心的一眼。
 */
export function presentsComic(input: PrivacyInput): boolean {
  const secrets = input.categoryIds.filter(id => input.privateCategoryIds.has(id))
  if (!secrets.length) return true
  return input.selectedCategoryIds.some(id => secrets.includes(id))
}

/**
 * 隱私名單的兩個來源：雲端目錄與裝置上的鏡像。
 *
 * 原本只看雲端，於是有兩個空窗——書庫剛開啟、本機作品已經畫出來而雲端清單還沒回來，
 * 以及雲端連不上。兩者都讓隱私集合是空的，等於這個功能完全沒生效（2026-09 實機回報）。
 * 鏡像存的是同一份資料、而且與本機作品同一批載入，用它當第二個來源不需要任何新請求。
 *
 * 一律取聯集：任一邊說是隱私就藏。藏錯邊的代價不對稱——鏡像過期而多藏一本，下一次載入
 * 就修正；少藏一本則是這個功能唯一要防的事。
 */
export function privateCategoryIds(sources: {
  cloudCategories: readonly { id: string; private: boolean }[]
  mirrorCategories: readonly { id: string; private: boolean }[]
}): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const category of sources.cloudCategories) if (category.private) ids.add(category.id)
  for (const category of sources.mirrorCategories) if (category.private) ids.add(category.id)
  return ids
}

/**
 * 這一格屬於哪些隱私分類。雲端成員名單與本機鏡像的指派同樣取聯集——已註冊的作品在雲端
 * 名單還沒回來時，靠本機那一份的指派就藏得住。
 */
export function privateCategoriesOf(input: {
  /** 雲端識別碼；集合格傳集合識別碼，未註冊的作品傳 null。 */
  cloudComicId: string | null
  /** 本機識別碼；只有雲端那一份的作品傳 null。 */
  localComicId: string | null
  privateCategoryIds: ReadonlySet<string>
  cloudMembers: readonly { categoryId: string; comicIds: readonly string[] }[]
  mirrorAssignments: readonly { comicId: string; categoryId: string }[]
}): string[] {
  const ids = new Set<string>()
  if (input.cloudComicId) {
    for (const group of input.cloudMembers) {
      if (input.privateCategoryIds.has(group.categoryId) && group.comicIds.includes(input.cloudComicId)) ids.add(group.categoryId)
    }
  }
  if (input.localComicId) {
    for (const item of input.mirrorAssignments) {
      if (item.comicId === input.localComicId && input.privateCategoryIds.has(item.categoryId)) ids.add(item.categoryId)
    }
  }
  return [...ids]
}

/** 集合格的隱私判斷：集合自己的分類指派，規則與作品相同。 */
export function privateCategoriesOfCollection(categoryIds: readonly string[], privateIds: ReadonlySet<string>): string[] {
  return categoryIds.filter(id => privateIds.has(id))
}
